import { CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';
import { JsonPipe, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    model,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import {
    MatListItem,
    MatListItemIcon,
    MatListItemLine,
    MatListItemTitle,
    MatListSubheaderCssMatStyler,
    MatSelectionList,
} from '@angular/material/list';
import { MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';

import { take } from 'rxjs/operators';

import { AngularNodeViewComponent, TiptapDraggableDirective } from 'ngx-tiptap';

@Component({
    selector: 'admin-tiptap-email-variable-container',
    templateUrl: './tiptap-email-variable-container.component.html',
    styleUrls: ['./tiptap-email-variable-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CdkConnectedOverlay,
        NgClass,
        FormsModule,

        MatSelectionList,
        MatDivider,
        MatListSubheaderCssMatStyler,
        MatListItemIcon,
        MatListItemTitle,
        MatListItemLine,
        MatListItem,
        MatIcon,
        MatButtonToggleGroup,
        MatButtonToggle,

        TiptapDraggableDirective,
    ],
})
export class TiptapEmailVariableContainerComponent extends AngularNodeViewComponent implements OnInit {
    overlayWidth = 400;
    positions: ConnectedPosition[] = [
        {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
        },
        {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
            panelClass: 'mat-mdc-select-panel-above',
        },
    ];

    cd = inject(ChangeDetectorRef);
    scrollStrategy = inject(MAT_SELECT_SCROLL_STRATEGY)();
    overlayDir = viewChild.required(CdkConnectedOverlay);

    isEditable = computed(() => this.editor().isEditable);

    formatAs = model<'date' | 'datetime'>('date');
    isVariableListPanelOpen = signal<boolean>(false);
    isOptionsPanelOpen = signal<boolean>(false);
    variable = signal<string>('');
    variableText = computed(() => {
        const variable = this.variable();
        const format = this.node().attrs['format'];
        return this.buildVariableText(variable, format);
    });
    hasOptions = computed(() => {
        switch (this.variable()) {
            case 'link.startDate':
                return true;
            default:
                return false;
        }
    });

    constructor() {
        super();
    }

    buildVariableText(variable: string, format: string): string {
        if (!variable) {
            return '{{ Click to change }}';
        }

        if (format) {
            if (format === 'date') {
                return `{{ ${variable} | time_zone: "Mountain Standard Time" | format_date:"MMMM dd", "en-us" }}`;
            } else if (format === 'datetime') {
                return `{{ ${variable} | time_zone: "Mountain Standard Time" | format_date:"MMMM dd, yyyy h:mm tt", "en-us" }}`;
            }
        }

        return `{{ ${variable} }}`;
    }

    ngOnInit(): void {
        this.formatAs.set(this.node().attrs['format'] || 'date');
        this.variable.set(this.node().attrs['variable']);

        // this.editor().on('transaction', ({ editor, transaction }) => {
        //     //this.updateZoomMeeting();
        //     this.cd.detectChanges();
        // });

        // this.editor().on('selectionUpdate', ({ editor, transaction }) => {
        //     //this.updateZoomMeeting();
        //     this.cd.detectChanges();
        // });
    }

    onVariableClick(event: MouseEvent): void {
        event.stopPropagation();

        if (this.editor().isEditable === false) {
            return;
        }

        this.isVariableListPanelOpen.set(true);
    }

    onOptionsClick(event: MouseEvent): void {
        event.stopPropagation();

        if (this.editor().isEditable === false) {
            return;
        }

        const variable = this.variable();

        switch (variable) {
            case 'link.startDate':
                {
                    const format = this.node().attrs['format'] || '';
                    this.formatAs.set(format === 'datetime' ? 'datetime' : 'date');
                }
                break;
        }

        this.isOptionsPanelOpen.set(true);
        this.cd.detectChanges();
    }

    onFormatChange(event: MatButtonToggleChange): void {
        // this.formatAs.set(event.value);
        const format = event.value;
        this.editor().chain().updateEmailVariable({ format }).run();
        this.cd.detectChanges();
    }

    onClosePanel(): void {
        if (this.isVariableListPanelOpen()) {
            this.isVariableListPanelOpen.set(false);
        }

        if (this.isOptionsPanelOpen()) {
            this.isOptionsPanelOpen.set(false);
        }
        this.cd.markForCheck();
    }

    onAttached(): void {
        this.overlayDir()
            .positionChange.pipe(take(1))
            .subscribe(() => {
                this.cd.detectChanges();
            });
    }

    updateVariable(variable: string): void {
        if (variable === 'zoomJoinUrl') {
            this.editor().commands.insertZoomJoinLink({ showJoinButton: false, showJoinUrl: true, renderAs: 'text' });
            // this.editor().commands.setNode('zoomLink');
            // this.editor().commands.setNode('zoomLink', { showJoinButton: false, showJoinUrl: true });
            return;
        }

        if (variable === 'emailReceipt') {
            this.editor().commands.insertEmailReceipt();
            return;
        }

        if (variable === 'emailRegistrants') {
            this.editor().commands.insertEmailRegistrants();
            return;
        }

        if (variable === 'emailFormAnswers') {
            this.editor().commands.insertEmailFormAnswers();
            return;
        }

        this.variable.set(variable);
        this.editor().chain().updateEmailVariable({ variable }).run();
        this.cd.detectChanges();
    }
}
